/**
 * What a failed callable tells Sentry about WHICH call failed and the device state at that moment (OFFICEHUB-CLIENT-71).
 *
 * A callable failure arrives as `FirebaseError: internal [0]` (the browser lost the fetch) with no stack frame for the call site and a
 * masked URL, so six recurrences on /forms/fill, /crm/leads and /invite could not be matched to a server log or a Cloud Run instance start.
 * The wrapper in lib/firebase/authReadyCallable.ts calls annotateCallableFailure() on every rejection; createCallableContextBeforeSend()
 * (src/index.tsx) copies the annotation into event tags. The annotation holds ONLY: the callable's name (a function identifier, never an
 * argument, payload or user data), document.visibilityState and navigator.onLine read at failure time. Those two separate "the app was in
 * the background / had no network" from "the server was cold" without guessing.
 *
 * The annotation is a non-enumerable Symbol.for property, so it never reaches JSON, console output or a payload.
 */
const KEY = Symbol.for('officehub.callableFailureContext');

export interface CallableFailureContext {
  readonly callable: string;
  readonly visibility: string;
  readonly online: string;
}

const MAX_NAME = 80;

/** A callable name from an SDK name or a router URL: the last path segment, no query, no fragment, bounded. */
export function callableLabel(nameOrUrl: string): string {
  const noQuery = nameOrUrl.split(/[?#]/)[0] ?? '';
  const last = noQuery.replace(/\/+$/, '').split('/').pop() ?? '';
  return last.slice(0, MAX_NAME) || 'unknown';
}

/** Marks `err` with the failing callable and the device state now. Returns `err` unchanged in identity; never throws (frozen or non-object errors are left alone). First annotation wins. */
export function annotateCallableFailure<E>(err: E, nameOrUrl: string): E {
  try {
    if ((typeof err !== 'object' && typeof err !== 'function') || err === null || KEY in (err as object)) return err;
    const ctx: CallableFailureContext = {
      callable: callableLabel(nameOrUrl),
      visibility: typeof document !== 'undefined' ? String(document.visibilityState) : 'unknown',
      online: typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? String(navigator.onLine) : 'unknown',
    };
    Object.defineProperty(err, KEY, { value: ctx, enumerable: false, configurable: true });
  } catch {
    /* telemetry must never change what the caller sees */
  }
  return err;
}

export function readCallableFailureContext(err: unknown): CallableFailureContext | null {
  if (typeof err !== 'object' || err === null) return null;
  const ctx = (err as Record<symbol, unknown>)[KEY];
  return ctx && typeof ctx === 'object' ? (ctx as CallableFailureContext) : null;
}

/**
 * Wraps an app's `beforeSend`: an event whose original exception carries the annotation gets three tags first. Other events pass through untouched.
 * Tagging is best effort and runs inside try/catch: a rejection reason with a throwing getter or a revoked Proxy must never stop the event
 * from reaching the inner beforeSend (that would drop the report, or throw inside the SDK's send path).
 */
export function withCallableFailureTags<E extends { tags?: Record<string, unknown> }, H extends { originalException?: unknown } | undefined, R>(
  beforeSend: (event: E, hint: H) => R
): (event: E, hint: H) => R {
  return (event, hint) => {
    let tagged: E = event;
    try {
      const ctx = readCallableFailureContext(hint?.originalException);
      if (ctx) {
        tagged = { ...event, tags: { ...event.tags, 'callable.name': ctx.callable, 'page.visibility': ctx.visibility, 'navigator.online': ctx.online } };
      }
    } catch {
      tagged = event;
    }
    return beforeSend(tagged, hint);
  };
}
